import { useAuth } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";

import { useUser } from "@/web/auth/useUser";
import { toaster } from "@/web/components/primitives/toast";
import {
  canSubmitPledge,
  PledgeDialog,
  type PledgeFormState,
} from "@/web/components/styled/PledgeForm";
import {
  buildPledgeFormState,
  hadExistingPledge,
  pledgeFormHasChanges,
  pledgeFormSubmitFields,
} from "@/web/components/styled/pledgeFormState";
import { ErrorHandler } from "@/web/lib/errors";
import { tableQueryOptions, useProcedureMutation } from "@/web/lib/tanstack";
import { parseCardId } from "@/web/routes/-shareableCardGate/shareableCardGate";
import { UnifiedBrowseCard } from "@/web/routes/browse/-components/cards/UnifiedBrowseCard";
import type { SharableBusinessCardBrowseResult } from "@/web/routes/browse/-components/types";
import { procedures, tables } from "@tycho/web-bindings";

export function SharableBusinessCardCard({
  browseResult,
}: {
  browseResult: SharableBusinessCardBrowseResult;
}) {
  const { sharableBusinessCard } = browseResult;
  // Password-protected cards still appear on `/browse` per product spec, but
  // the totals are gated behind the `/ttw/<cardId>` access-code prompt.
  const isLocked = browseResult.requiresAccessCode ?? false;
  const hasTtwPage = browseResult.hasTtwPage ?? false;

  // Link out only when there is a real TTW page to land on, or when the card is
  // password-protected. A public card without a page opens the inline pledge
  // dialog instead (the `/ttw/<cardId>` fallback is just the floating card).
  // Password-protected cards still link: the inline dialog can't work without
  // the access code, so the password gate on `/ttw/<cardId>` is the entry point.
  const linksOut = hasTtwPage || isLocked;

  const [pledgeDialogOpen, setPledgeDialogOpen] = useState(false);
  const openPledgeDialog = useCallback(() => setPledgeDialogOpen(true), []);

  const visualProps = {
    stage: "planning" as const,
    name: sharableBusinessCard.name,
    category: browseResult.businessTypeText || undefined,
    imageCdnPath: sharableBusinessCard.heroCdnPath,
    tint: sharableBusinessCard.colors?.primary,
    backerCount: browseResult.totalInterested,
    maskBackers: isLocked,
    locked: isLocked,
  };
  const cardBody = browseResult.hasTtwPageTargetProperty ? (
    <UnifiedBrowseCard entity="deal" address={browseResult.location} {...visualProps} />
  ) : (
    <UnifiedBrowseCard entity="business" {...visualProps} />
  );

  return (
    <>
      {/* `data-browse-card-action` lets the browse map forward a pin click to
          this card's primary action — link navigation when the card has a page
          (or is password-protected), otherwise opening the pledge dialog. */}
      {linksOut ? (
        <Link
          to="/ttw/$slug"
          params={{ slug: sharableBusinessCard.cardId }}
          target="_blank"
          rel="noopener noreferrer"
          className="block transition-opacity hover:opacity-95 focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={`View ${sharableBusinessCard.name} page${isLocked ? " (password required)" : ""}`}
          data-browse-card-action
        >
          {cardBody}
        </Link>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={openPledgeDialog}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") openPledgeDialog();
          }}
          className="cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2"
          data-browse-card-action
        >
          {cardBody}
        </div>
      )}

      {!linksOut ? (
        <SharableBusinessCardPledgeDialog
          open={pledgeDialogOpen}
          onOpenChange={setPledgeDialogOpen}
          cardId={sharableBusinessCard.cardId}
          businessName={sharableBusinessCard.name ?? "this business"}
          initialInterest={browseResult.userInterest}
        />
      ) : null}
    </>
  );
}

/**
 * Modal that records / updates the current user's card pledge via the shared
 * <PledgeDialog>, using `setInterestInBusinessCard` (the same procedure the TTW
 * recipient page uses). Mirrors `NominatedPledgeDialog`, but targets a
 * shareable business card rather than a nomination, and is only rendered for
 * public cards — password-protected cards link out to the `/ttw/<cardId>` gate
 * instead (the access code is a shared secret the browse client never holds).
 */
export function SharableBusinessCardPledgeDialog({
  open,
  onOpenChange,
  cardId,
  businessName,
  initialInterest,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardId: string;
  businessName: string;
  initialInterest: SharableBusinessCardBrowseResult["userInterest"];
}) {
  const { isSignedIn } = useAuth();
  const user = useUser();
  const queryClient = useQueryClient();
  const parsedCardId = parseCardId(cardId);

  // Destructure so the seed effect keys on primitive values, not the
  // `initialInterest` object's identity (which could differ per render).
  const initialAmount = initialInterest?.interestDollarAmount;
  const initialFirstName = initialInterest?.firstName;
  const initialLastName = initialInterest?.lastName;
  const initialSupportiveMessage = initialInterest?.supportiveMessage;
  const initialConsentToDisplay = initialInterest?.consentToDisplay;
  const hasStoredInterest = initialInterest !== undefined;

  const [formState, setFormState] = useState<PledgeFormState>(() => buildPledgeFormState({}, user));
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const initialFormStateRef = useRef<PledgeFormState | null>(null);

  useEffect(() => {
    if (open) {
      const initial = buildPledgeFormState(
        {
          amount: initialAmount,
          firstName: initialFirstName,
          lastName: initialLastName,
          supportiveMessage: initialSupportiveMessage,
          consentToDisplay: initialConsentToDisplay,
        },
        user,
        { exactStoredNames: hasStoredInterest },
      );
      setFormState(initial);
      initialFormStateRef.current = initial;
    } else {
      initialFormStateRef.current = null;
    }
  }, [
    open,
    initialAmount,
    initialFirstName,
    initialLastName,
    initialSupportiveMessage,
    initialConsentToDisplay,
    hasStoredInterest,
    user,
  ]);

  const { mutateAsync: setInterest, isPending } = useProcedureMutation(
    procedures.setInterestInBusinessCard,
  );
  const { mutateAsync: removeInterest, isPending: isDeleting } = useProcedureMutation(
    procedures.removeInterestInBusinessCard,
  );

  const hasChanges = pledgeFormHasChanges(formState, initialFormStateRef.current);
  const canSubmit =
    !isPending &&
    !isDeleting &&
    parsedCardId !== undefined &&
    hasChanges &&
    canSubmitPledge(formState, isSignedIn ?? false);

  const handleSubmit = useCallback(async () => {
    if (!parsedCardId || formState.amount === undefined) return;
    if (isPending || !canSubmitPledge(formState, isSignedIn ?? false)) return;
    const fields = pledgeFormSubmitFields(formState, isSignedIn ?? false);
    try {
      await setInterest({
        cardId: parsedCardId,
        accessCode: "",
        interestDollarAmount: BigInt(formState.amount),
        firstName: fields.firstName,
        lastName: fields.lastName,
        email: fields.email,
        supportiveMessage: fields.supportiveMessage,
        consentToDisplay: fields.consentToDisplay,
      });
      toaster({
        message: hadExistingPledge(initialFormStateRef.current)
          ? "Your pledge has been updated."
          : "Thanks — we've recorded your pledge.",
        variant: "success",
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.campaignBrowseShareableCards).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myBusinessCardInterests).queryKey,
        }),
      ]);
      onOpenChange(false);
      if (!isSignedIn) setAuthPromptOpen(true);
    } catch (error) {
      ErrorHandler.default(error);
    }
  }, [formState, isPending, isSignedIn, onOpenChange, parsedCardId, queryClient, setInterest]);

  const handleDelete = useCallback(async () => {
    if (!parsedCardId || isDeleting) return;
    try {
      await removeInterest({ cardId: parsedCardId });
      toaster({ message: "Your pledge has been removed.", variant: "success" });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.campaignBrowseShareableCards).queryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: tableQueryOptions(tables.myBusinessCardInterests).queryKey,
        }),
      ]);
      onOpenChange(false);
      initialFormStateRef.current = null;
    } catch (error) {
      ErrorHandler.default(error);
    }
  }, [isDeleting, onOpenChange, parsedCardId, queryClient, removeInterest]);

  return (
    <PledgeDialog
      open={open}
      onOpenChange={onOpenChange}
      formState={formState}
      onFormStateChange={setFormState}
      onSubmit={() => void handleSubmit()}
      isPending={isPending}
      isSignedIn={isSignedIn ?? false}
      currentPledgeAmount={initialInterest?.interestDollarAmount}
      businessName={businessName}
      canSubmit={canSubmit}
      authPromptOpen={authPromptOpen}
      onAuthPromptOpenChange={setAuthPromptOpen}
      onDelete={isSignedIn && initialInterest !== undefined ? () => void handleDelete() : undefined}
      isDeleting={isDeleting}
    />
  );
}
